<template>
  <v-container fluid class="trainings-hub">
    <v-row no-gutters align="center">
      <v-col cols="2">
        <v-btn icon @click="$router.push('/')" color="white">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
      </v-col>
      <v-col cols="8" class="text-center">
        <h5 class="font-weight-bold white--text">Dein Trainingshub</h5>
      </v-col>
      <v-col cols="2"></v-col>
    </v-row>

    <v-row no-gutters class="mt-4" v-if="workout">
      <v-col>
        <h4 class="font-weight-bold pb-2 px-0 white--text">Direkt ins Training</h4>
        <v-slide-group dark>
          <v-slide-item v-for="(split, index) in workout.splits.workouts" :key="index">
            <v-card
              @click="selectSplit(split)"
              class="ma-2 rounded-xl start-split-card"
              height="160"
              width="130"
              :style="getSplitCardStyle(split.fixedVisualization)">
              <div class="d-flex flex-column fill-height pa-3 card-content">
                <h6 class="font-weight-bold white--text mb-0">{{ split.title }}</h6>
                <v-spacer></v-spacer>
                <v-img :src="split.fixedVisualization.img" height="80" width="80" contain class="align-self-end split-card-img" />
              </div>
            </v-card>
          </v-slide-item>
        </v-slide-group>
      </v-col>
    </v-row>

    <v-row no-gutters class="mt-8" v-if="groupedSessions.length && false">
      <v-col>
        <h4 class="font-weight-bold pb-2 px-0 white--text">Deine Aktivität</h4>
        <TrainingCalendar :sessions="groupedSessions" />
      </v-col>
    </v-row>

    <div class="tabs-container mx-0 mt-8">
      <v-tabs v-model="tab" dark background-color="transparent" hide-slider grow>
        <v-tab>Trainings</v-tab>
        <v-tab>Übungen</v-tab>
      </v-tabs>
    </div>

    <v-tabs-items v-model="tab" class="transparent" touchless>
      <v-tab-item>
        <v-row no-gutters v-if="stackedCardSessions && stackedCardSessions.length" class="mt-4">
          <v-col cols="12">
            <TrainingsHubCardStack :cards="stackedCardSessions" />
          </v-col>
        </v-row>
        <v-row v-else class="text-center white--text mt-10">
          <v-col>
            <v-icon color="grey">mdi-chart-timeline-variant</v-icon>
            <p class="grey--text mt-2">Noch keine Trainings absolviert.</p>
          </v-col>
        </v-row>
      </v-tab-item>
      <v-tab-item>
        <v-row no-gutters v-if="stackedCardExercises && stackedCardExercises.length" class="mt-4">
          <v-col cols="12">
            <TrainingsHubCardStack exercises :cards="stackedCardExercises" />
          </v-col>
        </v-row>
        <v-row v-else class="text-center white--text mt-10">
          <v-col>
            <v-icon color="grey">mdi-weight-lifter</v-icon>
            <p class="grey--text mt-2">Noch keine Übungen getrackt.</p>
          </v-col>
        </v-row>
      </v-tab-item>
    </v-tabs-items>

    <v-dialog v-model="showContinueDialog" persistent max-width="380" dark>
      <v-card class="rounded-xl" style="background: #2a2a2a">
        <v-btn icon absolute top right class="mt-1 mr-1" @click="cancelDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>

        <v-card-title class="text-h6 font-weight-bold justify-center pt-6"> Aktives Training </v-card-title>
        <v-card-text class="text-center white--text mt-3">
          <p>
            Du hast ein laufendes Training
            <strong v-if="activeSplit" class="d-block my-2 text-h6" :style="{ color: activeSplitVisualization.color }">{{
              activeSplit.title
            }}</strong>
            <span v-if="activeWorkoutElapsedTime" class="d-block grey--text text--lighten-1">Gestartet vor {{ activeWorkoutElapsedTime }}.</span>
          </p>
          <p class="mt-4">Möchtest du fortfahren?</p>
        </v-card-text>
        <v-card-actions class="pa-4 d-flex justify-space-around">
          <v-btn dark color="red" class="font-weight-bold" @click="discardAndStartNew" text> Verwerfen & Neu </v-btn>
          <v-btn depressed class="font-weight-bold" :color="activeSplitVisualization.color" :class="continueButtonTextClass" @click="continueWorkout">
            <v-icon left>mdi-play-circle-outline</v-icon>
            Fortsetzen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import api from "@/api"
import TrainingsHubCardStack from "./trainingsHubCardStack.vue"
import TrainingCalendar from "./trainingCalendar.vue"
import { mapState, mapGetters, mapActions } from "vuex"
import moment from "moment"

// Hilfsfunktion zur Bestimmung der Helligkeit einer Farbe
function getLuminance(hexColor) {
  if (!hexColor) return 0
  hexColor = hexColor.replace("#", "")
  const r = parseInt(hexColor.substring(0, 2), 16)
  const g = parseInt(hexColor.substring(2, 4), 16)
  const b = parseInt(hexColor.substring(4, 6), 16)
  // Luminanzformel (vereinfacht)
  return 0.299 * r + 0.587 * g + 0.114 * b
}

export default {
  name: "TrainingsHub",
  components: {
    TrainingsHubCardStack,
    TrainingCalendar,
  },
  data() {
    return {
      tab: null,
      workout: null,
      workouts: [],
      groupedSessions: [],
      groupedExercises: [],
      showContinueDialog: false,
      pendingSplit: null,
    }
  },
  computed: {
    ...mapGetters("deviceStore", ["exerciseList"]),
    ...mapState("runWorkoutStore", ["activeSplit", "workoutStartTime"]),

    activeWorkoutElapsedTime() {
      if (!this.workoutStartTime) return null
      return moment(this.workoutStartTime).fromNow(true)
    },

    // NEU: Holt die Visualisierungs-Daten für den aktuellen Split
    activeSplitVisualization() {
      if (this.activeSplit && this.activeSplit.title) {
        return this.getTrainingPlanVisualization(this.activeSplit.title)
      }
      // Fallback-Werte
      return { color: "#333333", img: null }
    },

    // NEU: Bestimmt die passende Textfarbe (schwarz/weiss) für den Button
    continueButtonTextClass() {
      const luminance = getLuminance(this.activeSplitVisualization.color)
      // Schwellenwert 140: Ist die Farbe eher hell, wird schwarzer Text verwendet.
      return luminance > 140 ? "black--text" : "white--text"
    },

    stackedCardSessions() {
      const filtered = this.groupedSessions
        .filter((session) => session.execution)
        .map((card) => {
          card.backgroundColor = this.getTrainingPlanVisualization(card.execution.title).color
          card.img = this.getTrainingPlanVisualization(card.execution.title).img
          return card
        })
      return filtered
    },
    stackedCardExercises() {
      const filtered = this.groupedExercises
        .filter((exercise) => exercise.exercise)
        .map((exercise) => {
          exercise.exercise = this.exerciseList.find((ex) => ex._id === exercise.exercise)
          return exercise
        })
      return filtered
    },
  },
  async created() {
    this.workout = await api.fetchOwnWorkout()
    const existingSessions = await api.fetchExistingSessions()
    this.workouts = existingSessions.workouts
    this.groupedSessions = existingSessions.groupedSessions
    this.groupedExercises = existingSessions.groupedExercises
    this.setGroupedExercises(existingSessions.groupedExercises)
  },
  methods: {
    ...mapActions("runWorkoutStore", ["setActiveSplit", "setGroupedExercises", "resetWorkoutState", "setActivePlanID"]),

    cancelDialog() {
      this.showContinueDialog = false
      this.pendingSplit = null
    },

    selectSplit(split) {
      if (this.activeSplit && this.workoutStartTime) {
        this.pendingSplit = split
        this.showContinueDialog = true
      } else {
        this.startNewWorkout(split)
      }
    },

    continueWorkout() {
      this.showContinueDialog = false
      this.setActivePlanID(this.workout._id)

      this.$router.push({ name: "Workout_run" })
    },

    async discardAndStartNew() {
      await this.resetWorkoutState()
      this.showContinueDialog = false

      if (this.pendingSplit) {
        this.startNewWorkout(this.pendingSplit)
        this.pendingSplit = null
      }
    },

    async startNewWorkout(split) {
      await this.resetWorkoutState()

      this.setActivePlanID(this.workout._id)
      this.setActiveSplit(split)

      this.$router.push({ name: "Workout_run" })
    },

    getSplitCardStyle(visualization) {
      if (!visualization || !visualization.color) {
        return { background: "#333" }
      }
      const darkenColor = (color, percent) => {
        let f = parseInt(color.slice(1), 16),
          t = percent < 0 ? 0 : 255,
          p = percent < 0 ? percent * -1 : percent,
          R = f >> 16,
          G = (f >> 8) & 0x00ff,
          B = f & 0x0000ff
        return (
          "#" +
          (0x1000000 + (Math.round((t - R) * p) + R) * 0x10000 + (Math.round((t - G) * p) + G) * 0x100 + (Math.round((t - B) * p) + B))
            .toString(16)
            .slice(1)
        )
      }
      const color2 = darkenColor(visualization.color, 0.4)
      return {
        background: `linear-gradient(135deg, ${visualization.color} 0%, ${color2} 100%)`,
      }
    },
    getTrainingPlanVisualization(title) {
      const armSplitKeywords = [
        "arm",
        "arme",
        "bizeps",
        "trizeps",
        "bizepstraining",
        "trizepstraining",
        "oberarm",
        "unterarm",
        "armtraining",
        "armübung",
        "armübungen",
        "arm-workout",
        "arm-muskulatur",
        "arms",
        "biceps",
        "triceps",
        "upper arm",
        "forearm",
        "arm training",
        "arm workout",
        "arm exercises",
        "arm muscles",
      ]
      const legSplitKeywords = [
        "bein",
        "beine",
        "beinarbeit",
        "unterkörper",
        "oberschenkel",
        "waden",
        "hamstrings",
        "quadrizeps",
        "beintraining",
        "beinübungen",
        "bein-workout",
        "leg",
        "legs",
        "leg day",
        "leg workout",
        "leg training",
        "thigh",
        "calves",
        "quads",
        "lower body",
        "leg exercises",
      ]
      const chestSplitKeywords = [
        "brust",
        "push",
        "brusttraining",
        "brustübungen",
        "brust-workout",
        "pectoralis",
        "brustmuskulatur",
        "chest",
        "chest training",
        "chest workout",
        "pectoral",
        "pecs",
        "chest exercises",
      ]
      const backSplitKeywords = [
        "rücken",
        "pull",
        "rückentraining",
        "rückenübungen",
        "rücken-workout",
        "lats",
        "latissimus",
        "trapez",
        "trapezius",
        "back",
        "back training",
        "back workout",
        "upper back",
        "lower back",
        "back exercises",
      ]
      const shoulderSplitKeywords = [
        "schulter",
        "schultertraining",
        "schulterübungen",
        "schulter-workout",
        "deltoid",
        "deltoideus",
        "shoulder",
        "shoulder training",
        "shoulder workout",
        "deltoids",
        "shoulder exercises",
      ]
      const coreSplitKeywords = [
        "bauch",
        "bauchtraining",
        "bauchübungen",
        "bauch-workout",
        "sechpack",
        "magen",
        "core",
        "abs",
        "abdominals",
        "abs workout",
        "ab exercises",
        "sixpack",
        "ober",
      ]

      const lowerName = title.toLowerCase()
      if (legSplitKeywords.some((keyword) => lowerName.includes(keyword))) {
        return { img: "/img/workout/legSplit.png", color: "#1C4966" } // Hellblau
      }
      if (chestSplitKeywords.some((keyword) => lowerName.includes(keyword))) {
        return { img: "/img/workout/chestSplit.png", color: "#02734A" } // Hellgrün
      }
      if (backSplitKeywords.some((keyword) => lowerName.includes(keyword))) {
        return { img: "/img/workout/backSplit.png", color: "#F2B950" } // Hellgelb
      }
      if (shoulderSplitKeywords.some((keyword) => lowerName.includes(keyword))) {
        return { img: "/img/workout/backSplit.png", color: "#F2B950" } // Hellgelb
      }
      if (armSplitKeywords.some((keyword) => lowerName.includes(keyword))) {
        return { img: "/img/workout/armSplit.png", color: "#E5735D" } // Hellrosa
      }
      if (coreSplitKeywords.some((keyword) => lowerName.includes(keyword))) {
        return { img: "/img/workout/cardioSplit.png", color: "#F7B5A7" } // Helles Orange
      }
      return { img: "/img/workout/upperBodySplit.png", color: "#A3C6C4" } // Helles Lila
    },
  },
}
</script>

<style scoped>
.trainings-hub {
  background-color: rgb(30, 30, 30);
  color: white;
  min-height: 100vh;
}
.transparent {
  background-color: transparent !important;
}

.start-split-card {
  transition: all 0.3s ease-in-out;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  overflow: hidden;
  position: relative;
}
.card-content {
  position: relative;
  z-index: 1;
}
.start-split-card:hover {
  transform: translateY(-5px) scale(1.03);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
}
.start-split-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: radial-gradient(circle at top right, rgba(255, 255, 255, 0.25), transparent 40%);
  opacity: 0;
  transition: opacity 0.4s ease;
  z-index: 0;
}
.split-card-img {
  position: absolute;
  bottom: 5px;
  right: 5px;
  filter: drop-shadow(0px 5px 10px rgba(0, 0, 0, 0.3));
  transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}
.start-split-card:hover .split-card-img {
  transform: scale(1.15) rotate(8deg);
}

.tabs-container {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  padding: 4px;
  backdrop-filter: blur(10px);
}

::v-deep .v-tab {
  color: rgba(255, 255, 255, 0.7) !important;
  border-radius: 20px;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  text-transform: none;
  font-weight: 600;
}

::v-deep .v-tab--active {
  background-color: #f5f5f5;
  color: #121212 !important;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}

::v-deep .v-tab::before {
  display: none;
}
</style>
